import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, filter, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiResponse } from 'src/app/interfaces/base-entity';
import { SignalRService } from 'src/app/utils/SignalR.service';
import {
  CorpacerosSyncStartedEvent,
  CorpacerosSyncProgressEvent,
  CorpacerosSyncCompletedEvent,
  CorpacerosSyncFailedEvent,
  CorpacerosSyncStartResponse,
  InventorySyncLogsRequest,
  InventorySyncLogsResponse
} from '../models/corpaceros-sync.models';

@Injectable({
  providedIn: 'root'
})
export class CorpacerosSyncService {
  private http = inject(HttpClient);
  private signalRService = inject(SignalRService);
  private baseUrl = `${environment.host}/api/EppInventory`;

  private destroy$ = new Subject<void>();
  private currentSyncId: string | null = null;

  // Re-exponer observables del SignalRService global (Fix del backend)
  // Ahora los eventos llegan sin importar en qué página esté el usuario
  public syncStarted$ = this.signalRService.corpacerosSyncStartedEvent$;
  public syncProgress$ = this.signalRService.corpacerosSyncProgressEvent$;
  public syncCompleted$ = this.signalRService.corpacerosSyncCompletedEvent$;
  public syncFailed$ = this.signalRService.corpacerosSyncFailedEvent$;
  public syncPing$ = this.signalRService.corpacerosSyncPingEvent$;

  // Propiedades UI globales para toast (Fix 2: persiste al navegar)
  public syncProgress: number = 0;
  public syncDetail: string = '';

  constructor() {
    console.log('🏗️ CorpacerosSyncService constructor');
    console.log('✅ Usando handlers globales de SignalR - eventos funcionan en cualquier página');
  }

  /**
   * Método de prueba para verificar que SignalR funciona
   */
  testSignalRConnection(): Observable<any> {
    const testUrl = `${environment.host}/api/Notification/test-signalr`;
    console.log('🧪 Enviando prueba SignalR a:', testUrl);
    return this.http.post(testUrl, {});
  }

  /**
   * Inicia la sincronización con Corpaceros
   * Retorna el syncId inmediatamente (202 Accepted)
   */
  startSync(companyId: number, workCenterId: number, soloDelDia: boolean = false): Observable<CorpacerosSyncStartResponse> {
    const url = `${this.baseUrl}/sync-corpaceros/${companyId}`;
    const body = { workCenterId, soloDelDia };

    console.log('🚀 Iniciando sincronización Corpaceros:', {
      url,
      body,
      companyId,
      workCenterId,
      soloDelDia
    });

    return new Observable(observer => {
      this.http.post<{ data: CorpacerosSyncStartResponse }>(url, body).subscribe({
        next: (response) => {
          this.currentSyncId = response.data.syncId;
          console.log('📝 SyncId asignado:', this.currentSyncId);
          observer.next(response.data);
          observer.complete();
        },
        error: (error) => {
          console.error('❌ Error al iniciar sincronización:', error);
          observer.error(error);
        }
      });
    });
  }

  getLogs(request: InventorySyncLogsRequest): Observable<ApiResponse<InventorySyncLogsResponse>> {
    const url = `${this.baseUrl}/sync-corpaceros/logs`;
    return this.http.post<ApiResponse<InventorySyncLogsResponse>>(url, request);
  }

  /**
   * Limpia suscripciones y estado
   */
  cleanup(): void {
    this.currentSyncId = null;
    this.destroy$.next();
  }
}
